import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { devMode } from '@/auth/devMode';
import { Icon } from '@/components/Icon';
import { JumpPalette } from '@/components/JumpPalette';
import { ThemeMenuItems } from '@/components/ThemeToggle';
import { navPages } from '@/components/navItems';
import type { Me } from '@/types';

/**
 * The portal frame: slim top bar (brand, Ctrl+K jump, help, account menu) over a sidebar grouped
 * into Workspace and Administration. Below 900px the sidebar becomes a drawer behind the menu
 * button, so the page never scrolls sideways on a phone.
 */
export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  // Close the drawer whenever the route changes — tapping a link should land you on the page.
  useEffect(() => { setNavOpen(false); }, [location.pathname, location.search]);

  // Ctrl+K / Cmd+K from anywhere, including inside a text box: it's the documented shortcut and
  // no field here uses it for anything else.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Escape closes the drawer on a phone.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <>
      <header className="topbar">
        <button
          type="button"
          className="bar-btn nav-toggle"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          aria-controls="sidebar"
          onClick={() => setNavOpen(o => !o)}>
          <Icon name={navOpen ? 'close' : 'menu'} />
        </button>
        <Link to="/" className="brand">
          <div className="logo" aria-hidden="true">KT</div>
          <span className="brand-name">AI Gateway</span>
        </Link>
        <button
          type="button"
          className="jump-trigger"
          aria-label="Jump to a page, model or caller (Ctrl+K)"
          aria-haspopup="dialog"
          onClick={() => setPaletteOpen(true)}>
          <Icon name="search" size={16} />
          <span className="jump-trigger-text">Jump to…</span>
          <kbd>Ctrl K</kbd>
        </button>
        <div className="topbar-actions">
          <Link to="/get-started" className="bar-btn" aria-label="Help and API docs" title="Help and API docs">
            <Icon name="help" />
          </Link>
          <AccountMenu me={me} />
        </div>
      </header>

      <div className="shell">
        <Sidebar me={me} open={navOpen} />
        {navOpen && (
          <button type="button" className="scrim open" aria-label="Close navigation" tabIndex={-1}
            onClick={() => setNavOpen(false)} />
        )}
        <div className="shell-main">{children}</div>
      </div>

      {paletteOpen && <JumpPalette me={me} onClose={() => setPaletteOpen(false)} />}
    </>
  );
}

function Sidebar({ me, open }: { me: Me; open: boolean }) {
  const location = useLocation();
  const pendingCount = usePendingApprovals(me.isAdmin);
  const cls = ({ isActive }: { isActive: boolean }) => (isActive ? 'nav-item active' : 'nav-item');
  const inModels = location.pathname.startsWith('/admin/models');
  const params = new URLSearchParams(location.search);

  const sidebarPages = navPages.filter(p => !p.paletteOnly && p.group !== 'Help');
  const workspace = sidebarPages.filter(p => p.group === 'Workspace');
  const admin = sidebarPages.filter(p => p.group === 'Administration');

  return (
    <nav id="sidebar" className={open ? 'sidebar open' : 'sidebar'} aria-label="Main">
      <div className="nav-group">Workspace</div>
      {workspace.map(p => (
        <NavLink key={p.to} to={p.to} end={p.end} className={cls}>
          <Icon name={p.icon} />
          {p.label}
        </NavLink>
      ))}

      {me.isAdmin && (
        <>
          <div className="nav-group">Administration</div>
          {admin.map(p => (
            <div key={p.to}>
              <NavLink to={p.to} className={cls}>
                <Icon name={p.icon} />
                {p.label}
                {p.to === '/admin/approvals' && pendingCount > 0 && (
                  <span className="nav-count" aria-label={`${pendingCount} pending`}>{pendingCount}</span>
                )}
              </NavLink>
              {p.to === '/admin/models' && inModels && (
                <>
                  <Link to="/admin/models"
                    className={location.pathname === '/admin/models' && !params.has('add') && !params.get('status') ? 'nav-sub active' : 'nav-sub'}
                    aria-current={location.pathname === '/admin/models' && !params.has('add') && !params.get('status') ? 'page' : undefined}>
                    Catalogue
                  </Link>
                  <Link to="/admin/models?add=1" className={params.has('add') ? 'nav-sub active' : 'nav-sub'}>
                    Add models
                  </Link>
                  <Link to="/admin/models?status=attention"
                    className={params.get('status') === 'attention' ? 'nav-sub active' : 'nav-sub'}>
                    Needs attention
                  </Link>
                </>
              )}
            </div>
          ))}
        </>
      )}

      <div className="nav-foot">
        <Link to="/get-started">Get started guide</Link>
        <Link to="/get-started#step-7">API reference</Link>
      </div>
    </nav>
  );
}

/**
 * Pending access + budget requests, from the same two queries (same cache keys) the Approvals
 * page uses — so the badge costs nothing extra once that page has been opened, and the page's
 * approve/reject invalidations keep the count current.
 */
function usePendingApprovals(isAdmin: boolean): number {
  const api = useApi();
  const callers = useQuery({
    queryKey: ['admin-callers', 'Pending'],
    queryFn: () => api.adminListCallers('Pending'),
    enabled: isAdmin
  });
  const budgets = useQuery({
    queryKey: ['admin-budget-requests', 'Pending'],
    queryFn: () => api.adminListBudgetRequests('Pending'),
    enabled: isAdmin
  });
  return (callers.data?.length ?? 0) + (budgets.data?.length ?? 0);
}

function AccountMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const initials = me.displayName.split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // Move focus into the menu so keyboard users land on the first choice.
    wrapRef.current?.querySelector<HTMLElement>('.menu .menu-item')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Arrow keys move between items, as in any menu.
  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(wrapRef.current?.querySelectorAll<HTMLElement>('.menu .menu-item') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <div className="account" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="bar-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${me.displayName}`}
        onClick={() => setOpen(o => !o)}>
        <span className="avatar" aria-hidden="true">{initials}</span>
        <span className="account-name" aria-hidden="true">
          <span>{me.displayName}</span>
          <small>{me.isAdmin ? 'Administrator' : 'Member'}</small>
        </span>
      </button>
      {open && (
        <div className="menu" role="menu" aria-label="Account" onKeyDown={onMenuKey}>
          <div className="menu-head" role="presentation">
            <strong>{me.displayName}</strong>
            <span>{me.upn}</span>
          </div>
          <ThemeMenuItems />
          <div className="menu-sep" role="separator" />
          <Link to="/usage" className="menu-item" role="menuitem" onClick={() => setOpen(false)}>My usage</Link>
          <SignOutItem />
        </div>
      )}
    </div>
  );
}

function SignOutItem() {
  if (devMode) {
    return (
      <div className="menu-item" role="menuitem" aria-disabled="true" tabIndex={-1} style={{ cursor: 'default' }}>
        <span className="text-muted">Sign out (not available in dev mode)</span>
      </div>
    );
  }
  return <RealSignOut />;
}

function RealSignOut() {
  const { instance } = useMsal();
  return (
    <button type="button" className="menu-item" role="menuitem" onClick={() => instance.logoutRedirect()}>
      Sign out
    </button>
  );
}
